import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Theater, KeyRound } from "lucide-react";

const STAGE_IMG = "https://images.unsplash.com/photo-1613210434051-4b00d62d03fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxlbXB0eSUyMHRoZWF0ZXIlMjBzdGFnZSUyMGRhcmslMjBzcG90bGlnaHR8ZW58MHx8fHwxNzgzMzU0OTI3fDA&ixlib=rb-4.1.0&q=85";
const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function Login() {
  const { user, login, error, setError } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  // Cast-accept flow (Iter 59): AuditionResponse redirects here with the
  // temp password pre-filled so the actor can sign in with one click and
  // is then forced onto /change-password.
  const [email, setEmail] = useState(sp.get("email") || "");
  const [password, setPassword] = useState(sp.get("temp") || "");
  const [submitting, setSubmitting] = useState(false);
  const isFirstLogin = !!sp.get("temp");

  useEffect(() => {
    if (user && user.role) {
      if (user.must_change_password) { nav("/change-password", { replace: true }); return; }
      nav(user.role === "super_admin" ? "/platform/overview" : "/productions", { replace: true });
    }
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const u = await login(email, password);
    setSubmitting(false);
    if (!u) return;
    if (u.must_change_password) nav("/change-password", { replace: true });
    else nav(u.role === "super_admin" ? "/platform/overview" : "/productions", { replace: true });
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0A0A0A]">
      <div className="relative hidden md:flex flex-col justify-between p-10 overflow-hidden border-r border-white/10">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url(${STAGE_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md border border-white/15 grid place-items-center bg-[#121212]">
              <Sparkles className="w-4 h-4 text-gold" />
            </div>
            <div>
              <div className="font-display text-2xl tracking-tight">StageCall</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">Production Desk</div>
            </div>
          </div>
        </div>
        <div className="relative max-w-md">
          <h1 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
            Every cue, every call<br />
            <span className="text-gold">in one place.</span>
          </h1>
          <p className="mt-5 text-zinc-400 text-base leading-relaxed">
            Rehearsal calendars, cast schedules, production notes, and department communication —
            crafted for directors, stage managers, and the ensemble.
          </p>
          <div className="mt-8 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-zinc-500">
            <Theater className="w-4 h-4 text-gold" /> Curtain up.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6 animate-fade-up" data-testid="login-form">
          <div className="md:hidden flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-gold" />
            <span className="font-display text-2xl">StageCall</span>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Sign in</div>
            <h2 className="mt-2 font-display text-3xl tracking-tight font-medium">Welcome to the house.</h2>
            <p className="mt-2 text-sm text-zinc-400">Use your production credentials to enter the desk.</p>
          </div>

          {isFirstLogin && (
            <div data-testid="first-login-banner" className="rounded-md border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-gold">
              <div className="font-medium">You've accepted your role — welcome!</div>
              <div className="text-xs mt-1 text-gold/90">Your temporary password is filled in. Sign in and you'll be asked to set a new one.</div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-zinc-300 text-xs uppercase tracking-widest">Email</Label>
              <Input id="email" type="email" autoComplete="email" data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-2 bg-[#121212] border-white/10 text-white h-11 focus:border-gold focus:ring-1 focus:ring-gold" />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-300 text-xs uppercase tracking-widest">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" data-testid="login-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-2 bg-[#121212] border-white/10 text-white h-11 focus:border-gold focus:ring-1 focus:ring-gold" />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <button type="submit" disabled={submitting} data-testid="login-submit-btn"
            style={goldBtn}
            className="w-full h-11 rounded-md font-semibold transition-all hover:brightness-110">
            {submitting ? "Signing in…" : "Enter production desk"}
          </button>

          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-[0.28em]"><span className="bg-[#0A0A0A] px-3 text-zinc-500">or</span></div>
          </div>

          <Link
            to="/enroll"
            data-testid="self-enroll-link"
            className="w-full h-11 rounded-md border border-gold/40 text-gold hover:bg-gold/10 transition-all inline-flex items-center justify-center gap-2 font-medium"
          >
            <KeyRound className="w-4 h-4" /> Self-enroll with a code
          </Link>
          <div className="text-center">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-zinc-500 hover:text-white">Forgot your password?</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
